import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { isEqual, remove } from 'lodash-es';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RecurringTaskRunner, RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { Language } from '../models/enums/language.enum';
import { LocalizationState } from '../store/localization.state';

@Injectable({ providedIn: 'root' })
export class TranslationObserverService implements OnDestroy {
  private readonly DEFAULT_INTERVAL = moment.duration(5, 'minutes');
  private currentInterval: moment.Duration;
  private loadTranslationUpdatesRunner: RecurringTaskRunner<void, any[]>;

  private translationServices: TranslateService[] = [];

  public constructor(
    private readonly recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
    private readonly logger: ILogger,
    private readonly store: Store,
  ) {}

  public startUpdateSequence(interval?: moment.Duration): Observable<any[]> {
    this.currentInterval = interval || this.DEFAULT_INTERVAL;

    if (!this.loadTranslationUpdatesRunner) {
      this.loadTranslationUpdatesRunner = this.recurringTaskRunnerFactory.createRunner<void, any[]>(() => this.checkTranslations(), {
        canBeResumedAndPaused: true,
      });
    }
    this.loadTranslationUpdatesRunner.stop(undefined);
    return this.loadTranslationUpdatesRunner.start(undefined, this.currentInterval);
  }

  public stopUpdateSequence() {
    this.loadTranslationUpdatesRunner.stop();
  }

  public changeInterval(interval: moment.Duration) {
    if (!this.currentInterval || this.currentInterval.asMilliseconds() !== interval.asMilliseconds()) {
      this.startUpdateSequence(interval);
    }
  }

  public registerTranslateService(translateService: TranslateService) {
    this.translationServices.push(translateService);
  }

  public unregisterTranslateService(translateService: TranslateService) {
    remove(this.translationServices, translateService);
  }

  private checkTranslations() {
    const lang = this.store.selectSnapshot(LocalizationState.selectedLanguage) || Language.English;

    if (!this.translationServices.length) {
      return of(null);
    }

    return forkJoin(
      this.translationServices.map((translationService) => {
        const loader = translationService.currentLoader;
        const compiler = translationService.compiler;

        return loader.getTranslation(lang).pipe(
          tap((translationSet) => {
            try {
              const newTranslation = compiler.compileTranslations(translationSet, lang);
              const currentTranslation = translationService.store.translations[lang];

              if (!isEqual(newTranslation, currentTranslation)) {
                translationService.setTranslation(lang, translationSet, true);
              }
            } catch (err) {
              this.logger.exception(err);
            }
          }),
        );
      }),
    );
  }

  public ngOnDestroy(): void {
    this.translationServices = [];
    this.loadTranslationUpdatesRunner.stop();
    this.loadTranslationUpdatesRunner.ngOnDestroy();
  }
}
